<template>
  <div id="app" class="app">
    <NuxtLayout v-slot="{ className }" class="app__layout">
      <NuxtPage :class="className" />
    </NuxtLayout>

    <ClientOnly>
      <ModalsContainer />

      <Transition name="fade">
        <BundlesCookies v-if="!isCookiesAccepted" class="app__cookies" />
      </Transition>
    </ClientOnly>
  </div>
</template>

<script setup>
import localStorageKeys from '@/constants/local-storage-keys';

import { ModalsContainer } from 'vue-final-modal';

// i18n seo
const head = useLocaleHead({
  addSeoAttributes: true,
});
useHead({
  htmlAttrs: () => head.value.htmlAttrs,
});

const isCookiesAccepted = useLocalStorage(
  localStorageKeys.COOKIES_ACCEPTED,
  false,
);

const metricsLoadDelay = 5000;
const userInteractionEvents = [
  'mousemove',
  'scroll',
  'keydown',
  'click',
  'touchstart',
  'wheel',
];
let metricDelayTimer;

async function initMetrics() {
  const {
    public: { gtmID },
  } = useRuntimeConfig();

  const metrics = useMetrics({ gtmID });
  metrics.init();

  if (isCookiesAccepted.value) {
    try {
      await gtagPushConsent('consent', 'update', {
        ad_storage: 'granted',
        analytics_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
        functionality_storage: 'granted',
        personalization_storage: 'granted',
      });
    } catch (error) {
      console.log(error);
    }
  }
}

function triggerMetricsLoader() {
  initMetrics();
  clearTimeout(metricDelayTimer);

  userInteractionEvents.forEach((event) => {
    window.removeEventListener(event, triggerMetricsLoader, { passive: true });
  });
}

onMounted(() => {
  metricDelayTimer = setTimeout(initMetrics, metricsLoadDelay);

  userInteractionEvents.forEach((event) => {
    window.addEventListener(event, triggerMetricsLoader, { passive: true });
  });
});
</script>

<style scoped lang="scss">
.app {
  $parent: &;

  display: flex;
  flex-direction: column;

  &:deep(#{$parent}__layout) {
    flex-grow: 1;
  }

  &__cookies {
    position: fixed;
    bottom: em($gap-sm);
    left: 50%;
    z-index: 10;

    @include media-breakpoint-up(sm) {
      max-width: em(360);
      transform: translateX(-50%);
    }

    @include media-breakpoint-down(sm) {
      inset-inline-end: em($gap-sm);
      inset-inline-start: em($gap-sm);
    }
  }
}
</style>
